import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import _ from 'underscore';
import { ReactComponent as NoRoomIcon } from '../../assets/icon/noRoom.svg';
import AddRoomModal from './AddRoomModal';
import Cards from '../../design-components/Cards';
import ModalDelete from '../../components/ModalDelete';
import CategoryDropdown from '../../components/CategoryDropdown';
import ModalInformationCategoryForm from '../../components/ModalInformationCategoryForm';
import { ForwardIcon, LayerIcon } from '../../assets/icon';
import {
  fetchRooms,
  fetchRoomById,
  deleteRoom,
} from '../../state/actions/room';
import { fetchCategories, createCategory, deleteCategory, modifyCategory, setCategory } from '../../state/actions/categories';
import { fetchFloor } from '../../state/actions/floor';
import './RoomManagement.css';

export default function RoomManagement() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { siteId, siteName } = useParams();
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, room: null });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedRoomStatus, setSelectedRoomStatus] = useState('all');
  const [tempSelectedCategory, setTempSelectedCategory] = useState('all');
  const [tempSelectedFloor, setTempSelectedFloor] = useState('all');
  const [tempSelectedRoomStatus, setTempSelectedRoomStatus] = useState('all');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [floorDropdownOpen, setFloorDropdownOpen] = useState(false);
  const [roomStatusDropdownOpen, setRoomStatusDropdownOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState(false);
  const [modalDeleteCategory, setModalDeleteCategory] = useState({
    show: false,
    category: null,
  });

  const { rooms, loading, activeTab, categories, floors, userData, category, categoryLoading } = useSelector(
    (state) => ({
      rooms: state.room.rooms,
      loading: state.room.loading,
      activeTab: state.tab?.tab,
      categories: state.categories.list,
      floors: state.floor.list,
      userData: state.auth.userData,
      category: state.categories.category,
      categoryLoading: state.categories.loading,
    })
  );

  // Fetch categories and floors on mount
  useEffect(() => {
    if (siteId) {
      dispatch(fetchCategories({ siteId }));
      dispatch(fetchFloor({ siteId }));
    }
  }, [dispatch, siteId]);

  // Fetch rooms with debounced search and filters
  useEffect(() => {
    if (siteId) {
      const timeoutId = setTimeout(
        () => {
          dispatch(
            fetchRooms({
              siteId,
              search,
              categoryIds:
                selectedCategory !== 'all' ? [selectedCategory] : undefined,
              floorIds: selectedFloor !== 'all' ? [selectedFloor] : undefined,
              isRoomOpen:
                selectedRoomStatus === 'open' ? true :
                selectedRoomStatus === 'removed' ? false : undefined,
            })
          );
        },
        search ? 500 : 0
      ); // No delay for initial load, 500ms delay for search

      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, siteId, activeTab, search, selectedCategory, selectedFloor, selectedRoomStatus]);

  const handleSearch = (e) => {
    setSearchError(null);
    const value = e.target.value;
    // Remove regex validation for now since regexInput is not imported
    setSearch(value);
  };

  const onNewRoomHandler = () => {
    setEditingRoom(null);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRoom(null);
  };

  const handleModalSubmit = async () => {
    if (siteId) {
      dispatch(
        fetchRooms({
          siteId,
          search,
          categoryIds:
            selectedCategory !== 'all' ? [selectedCategory] : undefined,
          floorIds: selectedFloor !== 'all' ? [selectedFloor] : undefined,
          isRoomOpen:
            selectedRoomStatus === 'open' ? true :
            selectedRoomStatus === 'removed' ? false : undefined,
        })
      );
    }
  };

  const handleEditRoom = async (room) => {
    try {
      // Fetch the full room details
      dispatch(fetchRoomById(room._id));
      setEditingRoom(room);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching room details:', error);
    }
  };

  const handleDeleteRoom = (room) => {
    setDeleteModal({ isOpen: true, room });
  };

  const onDeleteItemHandler = async () => {
    try {
      dispatch(deleteRoom(deleteModal.room._id));
      // Refresh the room list
      if (siteId) {
        dispatch(
          fetchRooms({
            siteId,
            search,
            categoryIds:
              selectedCategory !== 'all' ? [selectedCategory] : undefined,
            floorIds: selectedFloor !== 'all' ? [selectedFloor] : undefined,
          })
        );
      }
      setDeleteModal({ isOpen: false, room: null });
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ isOpen: false, room: null });
  };

  const handleRoomClick = (room) => {
    history.push(`/${siteId}/information/${siteName}/room/${room._id}`);
  };

  // Category filter handlers
  const handleSetCategory = () => {
    setSelectedCategory(tempSelectedCategory);
    setCategoryDropdownOpen(false);
  };

  const getCategory = (categoryId) => {
    const findCategory = categories.find((cat) => cat._id === categoryId);
    return findCategory?.name;
  };

  // Floor filter handlers
  const handleSetFloor = () => {
    setSelectedFloor(tempSelectedFloor);
    setFloorDropdownOpen(false);
  };

  const getFloor = () => {
    const findFloor = floors.find((floor) => floor._id === selectedFloor);
    return findFloor?.name;
  };

  // Room Status filter handlers
  const handleSetRoomStatus = () => {
    setSelectedRoomStatus(tempSelectedRoomStatus);
    setRoomStatusDropdownOpen(false);
  };

  const getRoomStatusLabel = () => {
    if (selectedRoomStatus === 'open') return 'Open';
    if (selectedRoomStatus === 'removed') return 'Removed';
    return 'Room Status';
  };

  // Room status options
  const roomStatusOptions = [
    { _id: 'open', name: 'Open' },
    { _id: 'removed', name: 'Removed' },
  ];

  // Category handlers
  const handleNew = () => {
    dispatch(
      setCategory({
        _id: '',
        name: '',
        description: '',
      })
    );
    setModalCategory(true);
  };

  const onSubmitCategory = () => {
    const action = category._id ? modifyCategory : createCategory;
    dispatch(
      action(
        { 
          ...category, 
          siteId,
          ...(category._id && { id: category._id })
        },
        () => {
          setModalCategory(false);
          // Refresh categories list
          dispatch(fetchCategories({ siteId }));
        }
      )
    );
  };

  const handleSetCategoryData = (data) => {
    dispatch(setCategory(data));
  };

  const handleEditCategory = (category) => {
    dispatch(setCategory(category));
    setModalCategory(true);
  };

  const handleDeleteCategory = (category) => {
    setModalDeleteCategory({
      show: true,
      category,
    });
  };

  const onDeleteCategory = () => {
    dispatch(
      deleteCategory(
        modalDeleteCategory.category._id,
        siteId,
        () => {
          setModalDeleteCategory({ show: false, category: null });
          // Refresh categories list
          dispatch(fetchCategories({ siteId }));
        }
      )
    );
  };

  const onCloseModalCategoryHandler = () => {
    setModalDeleteCategory({
      show: false,
      category: null,
    });
  };

  return (
    <div style={{ padding: '20px' }}>
      <div className="screens__nav">
        <div className="screens__search-wrapper">
          <div className="search-and-filters">
            <div className="media-categories__search">
              <i className="feather-search" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearch}
              />
            </div>
            
            {/* Category Filter */}
            <div
              className="playlist-wrapper"
              onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
            >
              <div className="playlist-selection">
                <ForwardIcon />
                <span>
                  {selectedCategory === 'all'
                    ? 'All Categories'
                    : getCategory(selectedCategory)}
                </span>
              </div>
              <i
                className={`feather-chevron-down ${
                  categoryDropdownOpen ? 'playlist-open' : 'playlist-closed'
                }`}
              />

              <CategoryDropdown
                categories={categories}
                setTempSelectedCategory={(id) => setTempSelectedCategory(id)}
                modalOpen={categoryDropdownOpen}
                handleSetPlaylist={handleSetCategory}
                tempSelectedCategory={tempSelectedCategory}
                handleNew={userData?.role === 'superadmin' ? handleNew : null}
                handleEditCategory={userData?.role === 'superadmin' ? handleEditCategory : null}
                handleDeleteCategory={userData?.role === 'superadmin' ? handleDeleteCategory : null}
                filter
              />
            </div>

            {/* Floor Filter */}
            <div
              className="playlist-wrapper"
              onClick={() => setFloorDropdownOpen(!floorDropdownOpen)}
            >
              <div className="playlist-selection">
                <LayerIcon />
                <span>
                  {selectedFloor === 'all' ? 'All Floors' : getFloor()}
                </span>
              </div>
              <i
                className={`feather-chevron-down ${
                  floorDropdownOpen ? 'playlist-open' : 'playlist-closed'
                }`}
              />

              <CategoryDropdown
                categories={floors}
                setTempSelectedCategory={(id) => setTempSelectedFloor(id)}
                modalOpen={floorDropdownOpen}
                handleSetPlaylist={handleSetFloor}
                tempSelectedCategory={tempSelectedFloor}
                filter
                title="Floor"
              />
            </div>

            {/* Room Status Filter */}
            <div
              className="playlist-wrapper"
              onClick={() => setRoomStatusDropdownOpen(!roomStatusDropdownOpen)}
            >
              <div className="playlist-selection">
                <LayerIcon />
                <span>{getRoomStatusLabel()}</span>
              </div>
              <i
                className={`feather-chevron-down ${
                  roomStatusDropdownOpen ? 'playlist-open' : 'playlist-closed'
                }`}
              />

              <CategoryDropdown
                categories={roomStatusOptions}
                setTempSelectedCategory={(id) => setTempSelectedRoomStatus(id)}
                modalOpen={roomStatusDropdownOpen}
                handleSetPlaylist={handleSetRoomStatus}
                tempSelectedCategory={tempSelectedRoomStatus}
                filter
                title="Room Status"
              />
            </div>
          </div>
          {searchError && <div className="search-error">{searchError}</div>}
        </div>

        <div className="screens__button-group">
          <button className="button-primary" onClick={onNewRoomHandler}>
            <i className="feather-plus" /> Add Room
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="loading-container"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <p>Loading rooms...</p>
        </div>
      ) : rooms && rooms.length > 0 ? (
        <div className="media-list-content">
          {_.chunk(rooms, 3).map((chunk, index) => (
            <div className="media-list-chunk" key={index}>
              {chunk.map((room) => (
                <div key={room._id} className="media-list">
                  <Cards
                    data={room}
                    type="room"
                    isShowMedia={true}
                    editAction={() => handleEditRoom(room)}
                    deleteAction={() => handleDeleteRoom(room)}
                    cardClickAction={() => handleRoomClick(room)}
                    isEdit={true}
                    isDelete={true}
                    isPreview={false}
                    isDownload={false}
                    isClickable={true}
                  />
                </div>
              ))}
              {chunk.length < 2 && <div className="media-list-empty" />}
              {chunk.length < 3 && <div className="media-list-empty" />}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="no-room-found"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            textAlign: 'center',
            color: '#9E9E9E',
          }}
        >
          <NoRoomIcon />
          <p>No rooms registered</p>
        </div>
      )}

      <AddRoomModal
        isVisible={isModalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        editingRoom={editingRoom}
      />

      <ModalDelete
        isVisible={deleteModal.isOpen}
        title="Delete Room"
        subtitle={deleteModal.room?.hasBookings ? 
          `Warning: Room "${deleteModal.room?.name}" has existing or future bookings. Deleting this room will also delete all associated bookings permanently.` : 
          ''
        }
        onOk={onDeleteItemHandler}
        onCancel={onCloseModalHandler}
        loading={loading}
      />

      <ModalInformationCategoryForm
        visible={modalCategory}
        onCancel={() => setModalCategory(false)}
        onCreate={onSubmitCategory}
        category={category}
        setCategory={handleSetCategoryData}
        isEditing={!!category?._id}
      />

      <ModalDelete
        isVisible={modalDeleteCategory.show}
        onOk={onDeleteCategory}
        onCancel={onCloseModalCategoryHandler}
        title="Delete Category"
        subtitle={modalDeleteCategory.category ? modalDeleteCategory.category.name : ''}
        loading={categoryLoading}
      />
    </div>
  );
}
